import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Feather, ArrowRight } from 'lucide-react';

const sections = [
  {
    heading: 'The problem with only writing long',
    paras: [
      'Most of us who want to write novels do the sensible thing: we work on the novel. Chapter three for a month. A subplot for a season. And somewhere in that long middle we lose the one thing that actually makes a writer better — finishing.',
      'You cannot learn how to land an ending if you only reach one every two years. You cannot learn to open with intent if your last first page was written in a different decade of your life. Craft is built by repetition, and a novel offers almost none.',
    ],
  },
  {
    heading: 'What a thousand words forces you to do',
    paras: ['Flash fiction — anything from a few hundred words to about a thousand — is the novel in miniature. In a single sitting you must:'],
    bullets: [
      ['Open with intent.', 'There is no room for warming up. The first sentence carries weight or the piece dies.'],
      ['Turn the story.', 'Something must shift: a reveal, a reversal, a change of heart. You learn to feel where that hinge belongs.'],
      ['Land it.', 'Endings are the hardest thing in fiction, and flash gives you one to practise every single day.'],
      ['Cut.', 'With a hard ceiling, every sentence has to earn its place. That habit follows you back to your longer work.'],
    ],
    after: 'Do this for a season and you will notice it in your novel: tighter openings, braver cuts, endings that resonate instead of trailing off.',
  },
  {
    heading: 'The habit matters more than the talent',
    paras: ['The writers who improve are not the most gifted ones. They are the ones who show up. That is not a motivational poster; it is simply how skills work. So this app is built around the habit, not the masterpiece:'],
    bullets: [
      ['A spark, every day.', 'An AI muse offers three prompts across six genres and six moods. A blank page stops being an obstacle.'],
      ['A timer and a word goal.', 'Five, ten, fifteen or twenty minutes. Sprint, finish, save.'],
      ['A streak you can see.', 'Milestones — first hundred words, first thousand, first five stories — make regular practice visible.'],
      ['A shelf that grows.', 'Every story is saved. Illustrate it, share it with a link, or let it live quietly in your library.'],
    ],
  },
  {
    heading: 'What it costs',
    paras: [
      'Every new account starts with a seven‑day free trial: three AI prompts a day, plus the editor, timer, goals, library, sharing, prompt collections and the Prompt of the Day. No card needed.',
      'After the week, the AI muse pauses but everything you wrote stays and the writing tools keep working. To bring the prompts back there is one paid plan, bought as a block of time rather than a subscription that quietly renews: $5 for 3 months, $15 for 6 months, $30 for a year. When your time runs out you simply decide again.',
    ],
  },
];

const About = () => {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 relative overflow-hidden" data-testid="about-page">
      <div className="absolute top-0 -left-32 w-96 h-96 bg-orange-200 rounded-full opacity-40 blur-3xl" aria-hidden="true" />
      <nav className="relative z-10 container mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center space-x-2">
          <div className="bg-white/60 backdrop-blur-sm p-2 rounded-full shadow-sm"><Feather className="h-5 w-5 text-amber-900" /></div>
          <span className="font-display text-xl font-bold text-amber-950">Flash Fiction</span>
        </Link>
        <div className="flex items-center space-x-3">
          <Link to="/pricing"><Button variant="ghost" className="text-amber-900 hover:bg-amber-100" data-testid="about-nav-pricing">Pricing</Button></Link>
          <Link to={user ? '/dashboard' : '/login'}><Button className="bg-amber-900 hover:bg-amber-800 text-amber-50" data-testid="about-nav-cta">{user ? 'Dashboard' : 'Sign In'}</Button></Link>
        </div>
      </nav>

      <article className="relative z-10 container mx-auto px-6 pb-24 max-w-3xl">
        <p className="text-amber-800 text-sm font-medium tracking-wider uppercase mb-3">About</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black text-amber-950 leading-tight tracking-tight mb-8" data-testid="about-headline">
          Why novelists should write flash fiction every day
        </h1>
        <p className="text-xl text-amber-900/85 leading-relaxed font-serif-italic mb-12">
          A novel takes a year to teach you one lesson. A piece of flash fiction teaches you the same lesson before lunch. That is the whole argument for flash fiction, and it is why this app exists.
        </p>

        {sections.map((s) => (
          <section key={s.heading} className="mb-12" data-testid="about-section">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-amber-950 mb-4">{s.heading}</h2>
            {s.paras.map((p) => <p key={p.slice(0, 30)} className="text-lg text-amber-900/85 leading-relaxed mb-4">{p}</p>)}
            {s.bullets && (
              <ul className="space-y-3 my-6 pl-1">
                {s.bullets.map(([lead, text]) => (
                  <li key={lead} className="flex gap-3 text-lg text-amber-900/85 leading-relaxed">
                    <span className="mt-2.5 h-2 w-2 rounded-full bg-orange-500 shrink-0" />
                    <span><strong className="text-amber-950">{lead}</strong> {text}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.after && <p className="text-lg text-amber-900/85 leading-relaxed">{s.after}</p>}
          </section>
        ))}

        <section className="bg-amber-950 text-amber-50 rounded-3xl p-10 text-center" data-testid="about-cta">
          <h2 className="font-display text-3xl font-bold mb-2">Start with one.</h2>
          <p className="text-amber-200/80 mb-6">Not a novel. Not a collection. One story, under a thousand words, finished today. Then another tomorrow.</p>
          <Link to={user ? '/dashboard' : '/register'}>
            <Button size="lg" className="bg-orange-500 hover:bg-orange-400 text-white rounded-full px-8 group" data-testid="about-start-button">
              Start writing free
              <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </section>
      </article>
    </div>
  );
};

export default About;
